export const CELL_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  BUTTON: 'button',
  DIVIDER: 'divider',
  SOCIAL: 'social',
  SPACER: 'spacer',
  HTML: 'html',
  LOOP: 'loop',
};

export const uid = () => Math.random().toString(36).slice(2, 10);

const DEFAULT_WIDTH = 200;
const DEFAULT_HEIGHT = 100;
const MIN_WIDTH = 50;
const MIN_HEIGHT = 30;

export const createCell = (type, overrides = {}) => {
  const base = {
    id: uid(),
    type,
    x: 50,
    y: 50,
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
    minWidth: MIN_WIDTH,
    minHeight: MIN_HEIGHT,
    zIndex: 1,
    content: '',
    allowOverlap: false,
    styles: {
      backgroundColor: 'transparent',
      padding: '8px',
      borderRadius: '0px',
      border: 'none',
    },
  };

  const typeDefaults = {
    [CELL_TYPES.TEXT]: {
      content: '<p>Double Click to Enter Text...</p>',
      height: 60,
      styles: { ...base.styles, fontSize: '12px', color: '#333333' },
    },
    [CELL_TYPES.IMAGE]: {
      content: '',
      width: 200,
      height: 150,
      styles: { ...base.styles, padding: '0px' },
      imageUrl: '',
      imageAlt: '',
      redirectUrl: '',
    },
    [CELL_TYPES.BUTTON]: {
      content: 'Button',
      width: 150,
      height: 50,
      buttonUrl: '',
      buttonWidth: 40,
      buttonHeight: 15,
      styles: {
        ...base.styles,
        backgroundColor: '#7a6a55',
        color: '#ffffff',
        borderRadius: '10px',
        padding: '12px 20px',
        textAlign: 'center',
        fontWeight: '600',
      },
    },
    [CELL_TYPES.DIVIDER]: {
      content: '',
      width: 400,
      height: 20,
      dividerColor: '#7a6a55',
      dividerThickness: 2,
      styles: { ...base.styles, padding: '0px' },
    },
    [CELL_TYPES.SOCIAL]: {
      content: '',
      width: 250,
      height: 80,
      minHeight: 80,
      socialLinks: {
        linkedin: '',
        facebook: '',
        twitter: '',
        instagram: '',
        youtube: '',
      },
      socialOrder: ['linkedin', 'facebook', 'twitter', 'instagram', 'youtube'],
      iconColor: '#EED191',
      iconSpacing: 1,
      styles: { ...base.styles, textAlign: 'center' },
    },
    [CELL_TYPES.SPACER]: {
      content: '',
      width: 400,
      height: 40,
      styles: { ...base.styles, backgroundColor: 'transparent' },
    },
    [CELL_TYPES.HTML]: {
      content: '<div></div>',
      width: 300,
      height: 200,
    },
    [CELL_TYPES.LOOP]: {
      content: '',
      width: 500,
      height: 300,
      loopBlocks: [],
      styles: { ...base.styles, backgroundColor: '#f9f9f9', border: '2px dashed #ccc' },
    },
  };

  return {
    ...base,
    ...(typeDefaults[type] || {}),
    ...overrides,
    id: overrides.id || base.id,
  };
};

export const createEmptySchema = () => ({
  cells: [],
  canvasWidth: 600,
  canvasHeight: 800,
  bgColor: '#ffffff',
});

export const addCell = (schema, cell) => {
  const maxZIndex = schema.cells.reduce((max, c) => Math.max(max, c.zIndex || 1), 0);
  return {
    ...schema,
    cells: [...schema.cells, { ...cell, zIndex: maxZIndex + 1 }],
  };
};

export const updateCell = (schema, cellId, updates) => {
  const updateCellsRecursive = (cells) => {
    return cells.map((c) => {
      if (c.id === cellId) {
        if (updates.styles) {
          return {
            ...c,
            ...updates,
            styles: {
              ...c.styles,
              ...updates.styles,
            },
          };
        }
        return { ...c, ...updates };
      }

      if (c.type === CELL_TYPES.LOOP && c.loopBlocks) {
        return {
          ...c,
          loopBlocks: updateLoopBlocksRecursive(c.loopBlocks),
        };
      }

      return c;
    });
  };

  const updateLoopBlocksRecursive = (blocks) => {
    return blocks.map((block) => {
      let updatedBlock = { ...block };

      if (block.cells) {
        updatedBlock.cells = updateCellsRecursive(block.cells);
      }

      if (block.children) {
        updatedBlock.children = updateLoopBlocksRecursive(block.children);
      }

      return updatedBlock;
    });
  };

  return {
    ...schema,
    cells: updateCellsRecursive(schema.cells),
  };
};

export const removeCell = (schema, cellId) => {
  const removeCellsRecursive = (cells) => {
    return cells
      .filter((c) => c.id !== cellId)
      .map((c) => {
        if (c.type === CELL_TYPES.LOOP && c.loopBlocks) {
          return {
            ...c,
            loopBlocks: removeLoopBlocksRecursive(c.loopBlocks),
          };
        }
        return c;
      });
  };

  const removeLoopBlocksRecursive = (blocks) => {
    return blocks.map((block) => {
      let updatedBlock = { ...block };
      if (block.cells) {
        updatedBlock.cells = removeCellsRecursive(block.cells);
      }
      if (block.children) {
        updatedBlock.children = removeLoopBlocksRecursive(block.children);
      }
      return updatedBlock;
    });
  };

  return {
    ...schema,
    cells: removeCellsRecursive(schema.cells),
  };
};

export const bringToFront = (schema, cellId) => {
  const maxZIndex = schema.cells.reduce((max, c) => Math.max(max, c.zIndex || 1), 0);
  return updateCell(schema, cellId, { zIndex: maxZIndex + 1 });
};

export const sendToBack = (schema, cellId) => {
  const minZIndex = schema.cells.reduce((min, c) => Math.min(min, c.zIndex || 1), Infinity);
  return updateCell(schema, cellId, { zIndex: Math.max(1, minZIndex - 1) });
};

export const getCellById = (schema, cellId) => {
  const findRecursive = (cells) => {
    for (const c of cells) {
      if (c.id === cellId) return c;
      if (c.type === CELL_TYPES.LOOP && c.loopBlocks) {
        const foundInBlocks = findInBlocks(c.loopBlocks);
        if (foundInBlocks) return foundInBlocks;
      }
    }
    return null;
  };

  const findInBlocks = (blocks) => {
    for (const block of blocks) {
      if (block.cells) {
        const found = findRecursive(block.cells);
        if (found) return found;
      }
      if (block.children) {
        const found = findInBlocks(block.children);
        if (found) return found;
      }
    }
    return null;
  };
  return findRecursive(schema.cells);
};

export const cellsOverlap = (cell1, cell2) => {
  return !(
    cell1.x + cell1.width <= cell2.x ||
    cell2.x + cell2.width <= cell1.x ||
    cell1.y + cell1.height <= cell2.y ||
    cell2.y + cell2.height <= cell1.y
  );
};

export const getOverlappingCells = (schema, cellId) => {
  const cell = getCellById(schema, cellId);
  if (!cell) return [];
  return schema.cells.filter((c) => c.id !== cellId && cellsOverlap(cell, c));
};

export const findNonOverlappingPosition = (movingCell, otherCells, canvasWidth = 600) => {
  const blockingCells = otherCells.filter((c) => !c.allowOverlap);

  if (blockingCells.length === 0) {
    return { x: movingCell.x, y: movingCell.y };
  }

  let x = movingCell.x;
  let y = movingCell.y;
  let iterations = 0;
  const maxIterations = 50;

  while (iterations < maxIterations) {
    const testCell = { ...movingCell, x, y };
    const overlapping = blockingCells.filter((c) => cellsOverlap(testCell, c));

    if (overlapping.length === 0) break;

    const maxBottom = Math.max(...overlapping.map((c) => c.y + c.height));
    y = maxBottom + 5;
    iterations++;
  }

  return { x: Math.max(0, x), y: Math.max(0, y) };
};

export const pushOverlappingCellsDown = (cells, changedCellId) => {
  const updated = cells.map((c) => ({ ...c }));
  const GAP = 5;

  const resolve = (sourceId) => {
    const source = updated.find((c) => c.id === sourceId);
    if (!source) return;

    for (const target of updated) {
      if (target.id === sourceId || target.allowOverlap) continue;
      if (!cellsOverlap(source, target)) continue;

      const newY = source.y + source.height + GAP;
      if (newY > target.y) {
        target.y = newY;
        resolve(target.id);
      }
    }
  };

  resolve(changedCellId);

  const changedCell = updated.find((c) => c.id === changedCellId);
  if (!changedCell) return updated;

  const belowCells = updated
    .filter((c) => c.id !== changedCellId && !c.allowOverlap && c.y >= changedCell.y)
    .sort((a, b) => a.y - b.y);

  for (const cell of belowCells) {
    let highestAllowedY = 0;
    for (const other of updated) {
      if (other.id === cell.id || other.allowOverlap) continue;
      const hOverlap = !(cell.x + cell.width <= other.x || other.x + other.width <= cell.x);
      if (!hOverlap) continue;
      if (other.y + other.height <= cell.y) {
        highestAllowedY = Math.max(highestAllowedY, other.y + other.height + GAP);
      }
    }
    if (highestAllowedY < cell.y) {
      cell.y = highestAllowedY;
    }
  }

  return updated;
};

export const clampPosition = (cell, canvasWidth, canvasHeight) => ({
  x: Math.max(0, Math.min(cell.x, canvasWidth - cell.width)),
  y: Math.max(0, Math.min(cell.y, canvasHeight - cell.height)),
});

export const snapToGrid = (cell, gridSize = 10) => ({
  x: Math.round(cell.x / gridSize) * gridSize,
  y: Math.round(cell.y / gridSize) * gridSize,
});

export const duplicateCell = (schema, cellId, offsetX = 20, offsetY = 20) => {
  const cell = getCellById(schema, cellId);
  if (!cell) return schema;

  const newCell = {
    ...cell,
    id: uid(),
    x: cell.x + offsetX,
    y: cell.y + offsetY,
  };

  return addCell(schema, newCell);
};

export const sortCellsByZIndex = (cells) =>
  [...cells].sort((a, b) => (a.zIndex || 1) - (b.zIndex || 1));

export const stylesToCss = (styles) => {
  if (!styles) return '';
  return Object.entries(styles)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => {
      const cssKey = k.replace(/([A-Z])/g, '-$1').toLowerCase();
      return `${cssKey}: ${v}`;
    })
    .join('; ');
};

export const cssToStyles = (css) => {
  if (!css) return {};
  const styles = {};
  css.split(';').forEach((rule) => {
    const [key, value] = rule.split(':').map((s) => s.trim());
    if (key && value) {
      const jsKey = key.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      styles[jsKey] = value;
    }
  });
  return styles;
};

export default {
  CELL_TYPES,
  uid,
  createCell,
  createEmptySchema,
  addCell,
  updateCell,
  removeCell,
  bringToFront,
  sendToBack,
  getCellById,
  cellsOverlap,
  getOverlappingCells,
  findNonOverlappingPosition,
  pushOverlappingCellsDown,
  clampPosition,
  snapToGrid,
  duplicateCell,
  sortCellsByZIndex,
  stylesToCss,
  cssToStyles,
};
